import {AuthSnapshot, logout} from './auth.js';
import {api} from './api.js';
import {apiUrl} from './config.js';
import {navigate, routeLinks} from './router.js';
import {BRAND_APP_FOOTER, BRAND_NAME, BRAND_PRODUCT} from './brand.js';

/** 侧栏收起角标的纵向位置，单位像素。 */
const SIDEBAR_COLLAPSE_TOP_KEY = 'tax-assistant-sidebar-collapse-top';
let sidebarCollapseResizeBound = false;

/**
 * 把侧栏收起角标限制在侧栏高度内。
 * @param button 角标按钮
 * @param top 期望的上边距
 */
function placeSidebarCollapseButton(button: HTMLButtonElement, top: number): void {
  const rail = button.parentElement;
  const limit = Math.max(0, (rail?.clientHeight || 0) - button.offsetHeight);
  const next = Math.min(Math.max(0, Math.round(top)), limit);
  button.style.top = `${next}px`;
}

/**
 * 记住角标位置，并允许在侧栏边上上下拖动。单击仍只负责收起或展开。
 * @param button 角标按钮
 */
function bindSidebarCollapseDrag(button: HTMLButtonElement): void {
  const saved = Number(localStorage.getItem(SIDEBAR_COLLAPSE_TOP_KEY));
  if (localStorage.getItem(SIDEBAR_COLLAPSE_TOP_KEY) !== null && Number.isFinite(saved)) {
    placeSidebarCollapseButton(button, saved);
  }
  if (!sidebarCollapseResizeBound) {
    sidebarCollapseResizeBound = true;
    window.addEventListener('resize', () => {
      const current = document.querySelector<HTMLButtonElement>('#sidebarCollapseButton');
      if (!current) return;
      placeSidebarCollapseButton(current, current.offsetTop);
    });
  }
  let suppressClick = false;
  button.addEventListener('pointerdown', (event) => {
    if (event.button !== 0) return;
    const startY = event.clientY;
    const startTop = button.offsetTop;
    let moved = false;
    button.setPointerCapture(event.pointerId);
    /**
     * @param moveEvent 指针移动
     */
    const onMove = (moveEvent: PointerEvent): void => {
      const delta = moveEvent.clientY - startY;
      if (!moved && Math.abs(delta) <= 4) return;
      moved = true;
      button.classList.add('is-dragging');
      placeSidebarCollapseButton(button, startTop + delta);
    };
    /**
     * @param upEvent 指针松开
     */
    const onUp = (upEvent: PointerEvent): void => {
      button.removeEventListener('pointermove', onMove);
      button.removeEventListener('pointerup', onUp);
      button.removeEventListener('pointercancel', onUp);
      button.classList.remove('is-dragging');
      if (moved) {
        suppressClick = true;
        window.setTimeout(() => { suppressClick = false; }, 0);
        localStorage.setItem(SIDEBAR_COLLAPSE_TOP_KEY, String(button.offsetTop));
      }
      if (button.hasPointerCapture(upEvent.pointerId)) button.releasePointerCapture(upEvent.pointerId);
    };
    button.addEventListener('pointermove', onMove);
    button.addEventListener('pointerup', onUp);
    button.addEventListener('pointercancel', onUp);
  });
  button.addEventListener('click', (event) => {
    if (!suppressClick) return;
    suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  }, true);
}

/** 当前页面的消息刷新。换页后指向新的顶栏。 */
let refreshNotifications: (() => void) | null = null;
let notificationPollTimer = 0;
let notificationPollBound = false;

/**
 * 页面保持打开时继续检查团队消息，新申请不用手动刷新。
 */
function bindNotificationPolling(): void {
  if (!notificationPollBound) {
    notificationPollBound = true;
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) refreshNotifications?.();
    });
    window.addEventListener('focus', () => refreshNotifications?.());
  }
  window.clearInterval(notificationPollTimer);
  notificationPollTimer = window.setInterval(() => {
    if (document.hidden) return;
    refreshNotifications?.();
  }, 10000);
}

export function renderLayout(root: HTMLElement, auth: AuthSnapshot, content: string): void {
  // The historical shell is shared by every business page, including the
  // plan list. The plan list used to have a full-width exception, which
  // accidentally removed the sidebar from the live site.
  const isSpaceRoute = ['/tax-assistant/space', '/tax-assistant/enterprise-space'].includes(window.location.pathname);
  if (!isSpaceRoute) {
    document.body.classList.remove('space-scope-personal', 'space-scope-enterprise');
  }
  const isWorkspacePage = true;
  const userName = auth.user?.displayName || auth.user?.email || '当前用户';
  const userAvatarDataUrl = auth.user?.avatarDataUrl || '';
  const userAvatarInitial = escapeHtml((userName || '用').slice(0, 1));
  const userAvatarInner = userAvatarDataUrl
    ? `<img src="${escapeHtml(userAvatarDataUrl)}" alt="用户头像">`
    : userAvatarInitial;
  const selectedTaskId = localStorage.getItem(`tax-assistant-active-task:${auth.organization.id}`) || '';
  const selectedTaskName = localStorage.getItem(`tax-assistant-active-task-name:${auth.organization.id}`) || '';
  const selectedTaskType = localStorage.getItem(`tax-assistant-active-task-type:${auth.organization.id}`) || 'customs-invoice-matching';
  const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
  const isLegalReviewChild = currentPath.startsWith('/tax-assistant/legal-review/');
  const isLegalReviewRisksPage = currentPath === '/tax-assistant/legal-review/risks';
  const isLitigationPlanEditor = currentPath === '/tax-assistant/legal-litigation/plan' || currentPath.startsWith('/tax-assistant/legal-litigation/plan/');
  const isLegalLitigationPage = (currentPath === '/tax-assistant/legal-litigation' || currentPath.startsWith('/tax-assistant/legal-litigation/')) && !isLitigationPlanEditor;
  const topbarTitles: Record<string, string> = {'/tax-assistant/tasks':'任务管理','/tax-assistant/plans':'台账方案','/tax-assistant/declarations':'报关单识别','/tax-assistant/invoices':'发票识别','/tax-assistant/matching':'匹配核对','/tax-assistant/ledger':'台账生成','/tax-assistant/suppliers':'厂家管理','/tax-assistant/rebate':'退税申报','/tax-assistant/archive':'资料归档','/tax-assistant/legal-review':'合同文件审核','/tax-assistant/legal-review/settings':'默认任务方案','/tax-assistant/legal-review/deliverables':'成果归档','/tax-assistant/legal-litigation':'诉讼台账管理','/tax-assistant/legal-litigation/settings':'诉讼台账方案','/tax-assistant/legal-litigation/plan':'诉讼台账方案编辑','/tax-assistant/profile':'个人主页','/tax-assistant/space':'空间','/tax-assistant/enterprise-space':'所属团队','/tax-assistant/team-join':'加入团队','/tax-assistant/integrations':'外部接入','/tax-assistant/organization':'账号安全','/tax-assistant/enterprise-account':'团队账号管理','/tax-assistant/billing':'订单中心'};
  const topbarTitle = currentPath==='/tax-assistant/enterprise-account'
    ?'团队空间管理'
    :isLegalReviewRisksPage
      ?'审核结果'
      :isLitigationPlanEditor
        ?'诉讼台账方案编辑'
      :isLegalLitigationPage
        ?'诉讼台账管理'
      :(topbarTitles[currentPath] || (currentPath.startsWith('/tax-assistant/legal-review') ? '合同文件审核' : BRAND_NAME));
  const topbarHeading = escapeHtml(topbarTitle);
  const showBackButton = !['/tax-assistant/tasks', '/tax-assistant/plans', '/tax-assistant/declarations', '/tax-assistant/invoices', '/tax-assistant/matching', '/tax-assistant/ledger', '/tax-assistant/legal-review', '/tax-assistant/profile', '/tax-assistant/space', '/tax-assistant/enterprise-space', '/tax-assistant/integrations', '/tax-assistant/organization', '/tax-assistant/enterprise-account', '/tax-assistant/billing'].includes(currentPath) && !isLegalLitigationPage;
  const taskStatus = ['/tax-assistant/plans','/tax-assistant/profile','/tax-assistant/space','/tax-assistant/enterprise-space','/tax-assistant/organization','/tax-assistant/enterprise-account','/tax-assistant/billing'].includes(window.location.pathname) ? '' : `<div class="workspace-task-status ${selectedTaskId ? 'has-task' : 'is-empty'}" title="${escapeHtml(selectedTaskName)}"><span class="workspace-task-status-label">当前任务</span><span class="workspace-task-status-name">${selectedTaskId ? escapeHtml(selectedTaskName || '已选择任务') : '未选择任务'}</span></div>`;
  const isLegalReviewArchivePage = currentPath === '/tax-assistant/legal-review/deliverables';
  const isLegalReviewSettingsPage = currentPath === '/tax-assistant/legal-review/settings';
  const showFooter = window.location.pathname !== '/tax-assistant/enterprise-space'
    && (!isLegalReviewChild || isLegalReviewRisksPage || isLegalReviewArchivePage || isLegalReviewSettingsPage);
  const sidebarCollapsed = localStorage.getItem('tax-assistant-sidebar-collapsed') === '1';
  root.innerHTML = `
    <div class="app workspace-shell${sidebarCollapsed ? ' is-sidebar-collapsed' : ''}">
      <div class="sidebar-rail">
      <aside class="sidebar" id="workspaceSidebar">
        <div class="logo"><div class="logo-icon"><img src="/assets/origin-engine-logo.png" alt="" aria-hidden="true"></div><h1>${escapeHtml(BRAND_NAME)}</h1><p>${escapeHtml(BRAND_PRODUCT)}</p></div>
        <nav class="nav">${routeLinks(Boolean(selectedTaskId), selectedTaskType)}</nav>
        <div class="sidebar-footer"><button class="user-info user-profile-entry${['/tax-assistant/profile','/tax-assistant/enterprise-space'].includes(window.location.pathname) ? ' is-active' : ''}" id="userProfileEntry" type="button" aria-label="打开设定"><div class="user-avatar-mark">${userAvatarInner}</div><div class="user-profile-label"><strong>设定</strong></div></button></div>
      </aside>
      <button class="sidebar-collapse" id="sidebarCollapseButton" type="button" aria-controls="workspaceSidebar" aria-expanded="${sidebarCollapsed ? 'false' : 'true'}" title="${sidebarCollapsed ? '显示侧栏，可上下拖动' : '隐藏侧栏，可上下拖动'}" aria-label="${sidebarCollapsed ? '显示侧栏' : '隐藏侧栏'}"><svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M7.5 2.5 4 6l3.5 3.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
      </div>
      <main class="main${isLegalReviewArchivePage ? ' legal-review-archive-main' : ''}">
        <header class="topbar">${showBackButton ? `<button class="topbar-back" id="pageBackButton" type="button"><span class="topbar-back-icon">&#xE72B;</span>${isLitigationPlanEditor?'返回方案列表':isLegalReviewChild?'返回工作台':'返回任务管理'}</button>` : ''}<div class="topbar-title">${topbarHeading}</div><div class="topbar-right">${taskStatus}<button class="icon-btn notification-button" id="notificationButton" type="button">消息<span class="notification-badge" id="notificationUnreadCount" hidden>0</span></button><button class="icon-btn" id="helpButton" type="button">帮助</button><button class="icon-btn" id="plansLogoutButton" type="button">退出</button></div></header>
        <section class="content${isLegalReviewChild ? ` legal-review-result-content${isLegalReviewRisksPage ? ' legal-review-result-with-footer' : ''}${isLegalReviewArchivePage ? ' legal-review-archive-content' : ''}${isLegalReviewSettingsPage ? ' legal-review-settings-content' : ''}` : ''}">${content}</section>
        ${showFooter?`<footer class="app-footer" aria-label="系统底部备案信息"><div class="app-footer-center">${escapeHtml(BRAND_APP_FOOTER)}</div></footer>`:''}
        </main>
    </div><div class="account-popover" id="accountPopover" role="menu" aria-hidden="true"><div class="account-popover-head"><button type="button" class="account-popover-avatar${userAvatarDataUrl ? ' has-image' : ''}" id="accountPopoverAvatar" aria-label="${userAvatarDataUrl ? '查看头像' : '打开个人主页'}">${userAvatarInner}</button><div class="account-popover-head-meta" id="accountPopoverProfileLink" role="button" tabindex="0" aria-label="打开个人主页"><strong>${escapeHtml(userName)}</strong><small>${escapeHtml(auth.user.email)}</small></div></div><div class="account-popover-divider"></div><button type="button" class="account-popover-item${currentPath==='/tax-assistant/organization'?' is-current':''}" data-account-action="settings"><span class="account-popover-icon">⚙</span><span>账号安全</span><span class="account-popover-arrow">›</span></button><div class="account-popover-divider"></div><button type="button" class="account-popover-item is-danger" data-account-action="logout"><span class="account-popover-icon">↪</span><span>退出登录</span></button></div><div class="modal-overlay avatar-preview-overlay" id="avatarPreviewModal" aria-hidden="true"><div class="avatar-preview-dialog" role="dialog" aria-modal="true" aria-label="头像预览"><button type="button" class="modal-close avatar-preview-close" id="closeAvatarPreviewModal" aria-label="关闭">×</button><img id="avatarPreviewImage" alt="用户头像" src=""></div></div><div class="modal-overlay" id="notificationModal"><div class="modal notification-modal"><div class="modal-header"><div><h3>消息</h3><p class="modal-subtitle">团队邀请、加入申请处理结果与系统提醒。</p></div><button class="modal-close" id="closeNotificationModal">×</button></div><div class="modal-body" id="notificationList"><div class="legacy-empty"><p>正在加载消息…</p></div></div><div class="modal-footer"><button class="primary-button" id="confirmNotificationModal">关闭</button></div></div></div>`;
  const shell = root.querySelector<HTMLElement>('.workspace-shell');
  const collapseButton = root.querySelector<HTMLButtonElement>('#sidebarCollapseButton');
  /**
   * 同步侧栏收起状态与按钮文案。
   * @param collapsed 是否将侧栏移出可视区域左侧
   */
  const syncSidebarCollapsed = (collapsed: boolean): void => {
    shell?.classList.toggle('is-sidebar-collapsed', collapsed);
    localStorage.setItem('tax-assistant-sidebar-collapsed', collapsed ? '1' : '0');
    if (!collapseButton) return;
    collapseButton.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    const label = collapsed ? '显示侧栏' : '隐藏侧栏';
    collapseButton.title = `${label}，可上下拖动`;
    collapseButton.setAttribute('aria-label', label);
  };
  if (collapseButton) bindSidebarCollapseDrag(collapseButton);
  collapseButton?.addEventListener('click', () => {
    syncSidebarCollapsed(!shell?.classList.contains('is-sidebar-collapsed'));
  });
  const scrollContent = root.querySelector<HTMLElement>('.content');
  if (scrollContent) scrollContent.scrollTop = 0;
  window.scrollTo(0, 0);
  root.querySelectorAll<HTMLOptionElement>('#invoiceLedger option').forEach((option) => {
    option.textContent = option.textContent?.replace(/\s*·\s*v\d+\s*$/, '') ?? '';
  });
  queueMicrotask(() => root.querySelectorAll<HTMLOptionElement>('#invoiceLedgerPlan option').forEach((option) => {
    option.textContent = option.textContent?.replace(/\s*·\s*v\d+\s*$/, '') ?? '';
  }));
  if (!isLitigationPlanEditor) {
    root.querySelectorAll<HTMLElement>('.mapping-enterprise-field').forEach((field) => {
      const value = field.textContent?.trim() ?? '';
      const parts = value.split(/\s*\/\s*/);
      const fieldName = parts.length > 1 ? parts[parts.length - 1].trim() : value;
      field.textContent = /\.(xlsx|xlsm?|csv)$/i.test(fieldName) ? '未命名列' : fieldName;
    });
  }
  const templateUploadTitle = root.querySelector<HTMLElement>('#templateUploadTitle');
  if (templateUploadTitle) templateUploadTitle.textContent = '上传台账模版文件';
  root.querySelectorAll<HTMLElement>('.mapping-calculation-tools .mapping-tool-label').forEach((label) => { label.hidden = true; });
  root.querySelectorAll<HTMLElement>('.mapping-reference-tools').forEach((tools) => {
    tools.querySelectorAll('label').forEach((label) => {
      const select = label.querySelector('select');
      if (select) label.replaceWith(select);
    });
  });
  const structureGrid = root.querySelector<HTMLElement>('.template-structure-grid');
  const rowLabel = structureGrid?.classList.contains('template-structure-grid--litigation')
    ? null
    : structureGrid?.querySelector<HTMLElement>(':scope > label:nth-child(2)');
  const rowInputs = rowLabel ? [...rowLabel.querySelectorAll<HTMLInputElement>('input')] : [];
  if (rowLabel && rowInputs.length >= 2) {
    rowLabel.innerHTML = '<span class="structure-field-label">表头所在行<span class="req">*</span></span><span class="structure-field-label">数据起始行<span class="req">*</span></span><div class="template-structure-row"></div>';
    const row = rowLabel.querySelector('.template-structure-row');
    rowInputs.forEach(input => row?.append(input));
  }
  root.querySelectorAll<HTMLInputElement>('.mapping-table .map-enabled').forEach((checkbox) => {
    const row = checkbox.closest<HTMLElement>('[data-map-row]');
    if (!row || row.querySelector('.mapping-toggle')) return;
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'mapping-toggle';
    toggle.setAttribute('aria-label', '切换字段启用状态');
    const sourceSelect = row.querySelector<HTMLSelectElement>('.map-source');
    const sync = () => {
      const enabled = checkbox.checked;
      toggle.classList.toggle('is-enabled', enabled);
      toggle.classList.toggle('is-disabled', !enabled);
      toggle.textContent = '';
      toggle.setAttribute('aria-label', enabled ? '已启用，点击停用' : '已停用，数据来源为不抓取');
      toggle.setAttribute('title', enabled ? '已启用' : '不抓取该字段');
      toggle.setAttribute('aria-pressed', String(enabled));
    };
    toggle.addEventListener('click', () => {
      checkbox.checked = !checkbox.checked;
      if (!checkbox.checked && sourceSelect) sourceSelect.value = '';
      sync();
    });
    checkbox.insertAdjacentElement('afterend', toggle);
    if (!checkbox.checked && sourceSelect) sourceSelect.value = '';
    sync();
  });
  root.querySelectorAll<HTMLSelectElement>('.mapping-table .map-source').forEach((select) => {
    select.addEventListener('change', () => {
      const row = select.closest<HTMLElement>('[data-map-row]');
      const checkbox = row?.querySelector<HTMLInputElement>('.map-enabled');
      if (checkbox && row) {
        checkbox.checked = Boolean(select.value);
        const toggle = row.querySelector<HTMLButtonElement>('.mapping-toggle');
        if (toggle) { toggle.classList.toggle('is-enabled', checkbox.checked); toggle.classList.toggle('is-disabled', !checkbox.checked); toggle.textContent = ''; toggle.setAttribute('aria-label', checkbox.checked ? '已启用，点击停用' : '已停用，点击启用'); toggle.setAttribute('title', checkbox.checked ? '已启用' : '已停用'); toggle.setAttribute('aria-pressed', String(checkbox.checked)); }
      }
    });
  });
  root.querySelector('#suggestFields')?.addEventListener('click', () => window.setTimeout(() => {
    root.querySelectorAll<HTMLInputElement>('.mapping-table .map-enabled').forEach((checkbox) => {
      const toggle = checkbox.closest<HTMLElement>('[data-map-row]')?.querySelector<HTMLButtonElement>('.mapping-toggle');
      if (toggle) { toggle.classList.toggle('is-enabled', checkbox.checked); toggle.classList.toggle('is-disabled', !checkbox.checked); toggle.textContent = ''; toggle.setAttribute('aria-label', checkbox.checked ? '已启用，点击停用' : '已停用，点击启用'); toggle.setAttribute('title', checkbox.checked ? '已启用' : '已停用'); toggle.setAttribute('aria-pressed', String(checkbox.checked)); }
    });
  }, 0));
  root.querySelectorAll<HTMLAnchorElement>('[data-route]').forEach((link) => link.addEventListener('click', (event) => {event.preventDefault(); navigate(link.href); }));
  root.querySelector('#plansLogoutButton')?.addEventListener('click', async () => {await logout(); window.location.assign('/login');});
  root.querySelector('#pageBackButton')?.addEventListener('click', () => navigate(isLitigationPlanEditor ? '/tax-assistant/plans?kind=legal-litigation' : isLegalReviewChild ? '/tax-assistant/legal-review' : '/tax-assistant/tasks'));
  const accountPopover = root.querySelector<HTMLElement>('#accountPopover');
  const accountButton = root.querySelector<HTMLElement>('#userProfileEntry');
  const serviceItem=document.createElement('button'); serviceItem.type='button'; serviceItem.className='account-popover-item'; serviceItem.dataset.accountAction='service'; serviceItem.innerHTML='<span class="account-popover-icon">✦</span><span>订单中心</span><span class="account-popover-arrow">›</span>'; accountPopover?.querySelector('.account-popover-divider')?.insertAdjacentElement('afterend',serviceItem);
  const enterpriseSpaceItem=document.createElement('button');enterpriseSpaceItem.type='button';enterpriseSpaceItem.className='account-popover-item';enterpriseSpaceItem.dataset.accountAction='enterprise-space';enterpriseSpaceItem.innerHTML='<span class="account-popover-icon">⌂</span><span>所属团队</span><span class="account-popover-arrow">›</span>';serviceItem.insertAdjacentElement('afterend',enterpriseSpaceItem);
  const integrationsItem=document.createElement('button');integrationsItem.type='button';integrationsItem.className=`account-popover-item${currentPath==='/tax-assistant/integrations'?' is-current':''}`;integrationsItem.dataset.accountAction='integrations';integrationsItem.innerHTML='<span class="account-popover-icon">⇄</span><span>外部接入</span><span class="account-popover-arrow">›</span>';enterpriseSpaceItem.insertAdjacentElement('afterend',integrationsItem);
  const closeAccountPopover = () => { accountPopover?.classList.remove('is-open'); accountPopover?.setAttribute('aria-hidden', 'true'); accountButton?.setAttribute('aria-expanded', 'false'); };
  accountButton?.addEventListener('click', (event) => { event.stopPropagation(); const alreadyOpen = accountPopover?.classList.contains('is-open'); if (alreadyOpen) { closeAccountPopover(); navigate('/tax-assistant/profile'); return; } accountPopover?.classList.add('is-open'); accountPopover?.setAttribute('aria-hidden', 'false'); accountButton?.setAttribute('aria-expanded', 'true'); });
  const avatarPreviewModal = root.querySelector<HTMLElement>('#avatarPreviewModal');
  const avatarPreviewImage = root.querySelector<HTMLImageElement>('#avatarPreviewImage');
  const onAvatarPreviewKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    closeAvatarPreview();
  };
  const openAvatarPreview = (avatarSrc: string) => {
    if (!avatarPreviewModal || !avatarPreviewImage || !avatarSrc) return;
    avatarPreviewImage.src = avatarSrc;
    avatarPreviewModal.classList.add('show');
    avatarPreviewModal.setAttribute('aria-hidden', 'false');
    document.addEventListener('keydown', onAvatarPreviewKeydown);
  };
  const closeAvatarPreview = () => {
    avatarPreviewModal?.classList.remove('show');
    avatarPreviewModal?.setAttribute('aria-hidden', 'true');
    document.removeEventListener('keydown', onAvatarPreviewKeydown);
  };
  const goAccountProfile = () => { closeAccountPopover(); navigate('/tax-assistant/profile'); };
  root.querySelector('#accountPopoverProfileLink')?.addEventListener('click', goAccountProfile);
  root.querySelector('#accountPopoverProfileLink')?.addEventListener('keydown', (event) => {
    if (event instanceof KeyboardEvent && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      goAccountProfile();
    }
  });
  root.querySelector('#accountPopoverAvatar')?.addEventListener('click', (event) => {
    event.stopPropagation();
    const img = root.querySelector<HTMLImageElement>('#accountPopoverAvatar img');
    const liveAvatar = img?.currentSrc || img?.src || '';
    if (liveAvatar) {
      closeAccountPopover();
      openAvatarPreview(liveAvatar);
      return;
    }
    goAccountProfile();
  });
  root.querySelector('#closeAvatarPreviewModal')?.addEventListener('click', closeAvatarPreview);
  avatarPreviewModal?.addEventListener('click', (event) => { if (event.target === avatarPreviewModal) closeAvatarPreview(); });
  accountPopover?.addEventListener('click', async (event) => { const action = (event.target as HTMLElement).closest<HTMLElement>('[data-account-action]')?.dataset.accountAction; if (!action) return; closeAccountPopover(); if (action === 'service') navigate('/tax-assistant/billing'); else if(action==='enterprise-space')navigate('/tax-assistant/enterprise-space'); else if(action==='integrations')navigate('/tax-assistant/integrations'); else if(action==='enterprise-account')navigate('/tax-assistant/enterprise-account'); else if (action === 'settings') navigate('/tax-assistant/organization'); else if (action === 'logout') { await logout(); window.location.assign('/login'); } });
  document.addEventListener('click', closeAccountPopover);
  const notificationModal = root.querySelector<HTMLElement>('#notificationModal');
  const notificationBadge=root.querySelector<HTMLElement>('#notificationUnreadCount');
  const notificationList=root.querySelector<HTMLElement>('#notificationList');
  type NotificationItem={id:string;kind:'team_invite'|'team_apply_pending'|'team_apply_approved'|'team_apply_rejected'|'service_granted'|'ledger_collaborator';organizationName:string;senderName:string;serviceName?:string;createdAt:string;readAt:string|null};
  const notificationTitle=(item:NotificationItem)=>{
    if(item.kind==='team_invite')return `${item.organizationName} 邀请你加入团队`;
    if(item.kind==='team_apply_pending')return `${item.senderName} 申请加入「${item.organizationName}」`;
    if(item.kind==='team_apply_approved')return `团队「${item.organizationName}」已通过你的加入申请`;
    if(item.kind==='service_granted')return `团队「${item.organizationName}」已为你开通「${item.serviceName||'一项服务'}」`;
    if(item.kind==='ledger_collaborator')return `你已被添加为诉讼台账「${item.serviceName||'诉讼台账'}」的协作人员`;
    return `加入「${item.organizationName}」的申请结果`;
  };
  const notificationMeta=(item:NotificationItem)=>{
    const time=escapeHtml(new Date(item.createdAt).toLocaleString('zh-CN'));
    if(item.kind==='team_invite')return `邀请人：${escapeHtml(item.senderName)} · ${time}`;
    if(item.kind==='team_apply_pending')return `申请人：${escapeHtml(item.senderName)} · ${time}`;
    if(item.kind==='team_apply_rejected')return `你好，感谢你申请加入「${escapeHtml(item.organizationName)}」。团队管理员已经查看，这次暂时无法通过。感谢你的理解，之后仍可以再次申请。${time}`;
    if(item.kind==='service_granted')return `授权人：${escapeHtml(item.senderName)} · ${time}`;
    if(item.kind==='ledger_collaborator')return `「${escapeHtml(item.organizationName)}」 · 添加人：${escapeHtml(item.senderName)} · ${time}`;
    return `处理人：${escapeHtml(item.senderName)} · ${time}`;
  };
  const renderNotificationList=(items:NotificationItem[])=>{if(!notificationList)return;notificationList.innerHTML=items.length?items.map(item=>{const unread=item.kind==='team_invite'||item.kind==='team_apply_pending'||!item.readAt;const needsRespond=item.kind==='team_invite'||item.kind==='team_apply_pending';const approveLabel=item.kind==='team_apply_pending'?'同意加入':'接受邀请';const rejectLabel=item.kind==='team_apply_pending'?'拒绝申请':'拒绝';const actions=needsRespond?`<div class="notification-item-actions"><button type="button" class="primary-button" data-membership-respond="${escapeHtml(item.id)}" data-respond-kind="${escapeHtml(item.kind)}" data-approved="true">${approveLabel}</button><button type="button" class="secondary-button" data-membership-respond="${escapeHtml(item.id)}" data-respond-kind="${escapeHtml(item.kind)}" data-approved="false">${rejectLabel}</button></div>`:'';return `<article class="notification-item${unread?' is-unread':''}" data-notification-id="${escapeHtml(item.id)}"><strong>${escapeHtml(notificationTitle(item))}</strong><p>${notificationMeta(item)}</p>${actions}</article>`;}).join(''):'<div class="legacy-empty"><p>暂无消息记录</p></div>';};
  const syncNotificationBadge=(unreadCount:number)=>{if(!notificationBadge)return;notificationBadge.hidden=!unreadCount;notificationBadge.textContent=String(unreadCount||'');};
  const loadNotifications=async(options:{markApplyResultsRead?:boolean;quiet?:boolean}={})=>{try{const result=await api.get<{notifications:NotificationItem[];unreadCount:number}>(apiUrl('/api/organizations/notifications'));let items=result.notifications??[];syncNotificationBadge(result.unreadCount??0);renderNotificationList(items);if(options.markApplyResultsRead){const applyIds=items.filter(item=>(item.kind==='team_apply_approved'||item.kind==='team_apply_rejected'||item.kind==='service_granted'||item.kind==='ledger_collaborator')&&!item.readAt).map(item=>item.id);if(applyIds.length){await api.post(apiUrl('/api/organizations/notifications/read'),{ids:applyIds});const refreshed=await api.get<{notifications:NotificationItem[];unreadCount:number}>(apiUrl('/api/organizations/notifications'));items=refreshed.notifications??[];syncNotificationBadge(refreshed.unreadCount??0);renderNotificationList(items);}}}catch{if(!options.quiet&&notificationList)notificationList.innerHTML='<div class="legacy-empty"><p>消息暂时无法加载</p></div>';}};
  refreshNotifications=()=>{void loadNotifications({quiet:true});};
  bindNotificationPolling();
  void loadNotifications();
  const closeNotification = () => notificationModal?.classList.remove('show');
  root.querySelector('#notificationButton')?.addEventListener('click', () => {notificationModal?.classList.add('show');void loadNotifications({markApplyResultsRead:true});});
  root.querySelector('#closeNotificationModal')?.addEventListener('click', closeNotification);
  root.querySelector('#confirmNotificationModal')?.addEventListener('click', closeNotification);
  notificationList?.addEventListener('click', async event=>{
    const button=(event.target as HTMLElement).closest<HTMLButtonElement>('[data-membership-respond]');
    if(!button)return;
    const requestId=button.dataset.membershipRespond||'';
    const approved=button.dataset.approved==='true';
    try{
      button.disabled=true;
      await api.post(apiUrl(`/api/organizations/membership-requests/${encodeURIComponent(requestId)}/respond`),{approved});
      await loadNotifications();
      if(approved){
        window.alert(button.dataset.respondKind==='team_apply_pending'?'已同意该成员加入团队。':'已加入团队，可在「所属团队」中查看与管理。');
      }
    }catch(error){
      window.alert(error instanceof Error?error.message:String(error));
      button.disabled=false;
    }
  });
  root.querySelector('#helpButton')?.addEventListener('click', () => window.alert('帮助文档正在按历史版业务流程整理。'));
}

export function escapeHtml(value: unknown): string {
  return String(value ?? '').replace(/[&<>"']/g, (character) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[character] ?? character));
}

/**
 * 同步侧栏与账户菜单中的头像展示（个人主页保存头像后调用）。
 */
export function syncLayoutUserAvatars(avatarDataUrl: string, displayName: string, email: string): void {
  const fallback = escapeHtml((displayName || email || '用').slice(0, 1));
  const inner = avatarDataUrl
    ? `<img src="${escapeHtml(avatarDataUrl)}" alt="用户头像">`
    : fallback;
  document.querySelectorAll('#userProfileEntry .user-avatar-mark').forEach((mark) => { mark.innerHTML = inner; });
  const popoverAvatar = document.querySelector<HTMLButtonElement>('#accountPopoverAvatar');
  if (!popoverAvatar) return;
  popoverAvatar.innerHTML = inner;
  popoverAvatar.classList.toggle('has-image', Boolean(avatarDataUrl));
  popoverAvatar.setAttribute('aria-label', avatarDataUrl ? '查看头像' : '打开个人主页');
}
